/**
 * Triage Relationships Panel
 *
 * Collapsible panel surfaced inside the Triage Queue detail view.
 * Lets triagers attach canonical relationships (cluster, package, dependency,
 * aggregation, elaboration, lineage) to an item without leaving the triage
 * flow. All modals are the existing canonical ones; this component is purely
 * an orchestration layer.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  GitMerge,
  ChevronDown,
  ChevronUp,
  Plus,
  GitBranch,
  Layers,
  Link2,
  Package,
  Puzzle,
  Clock,
  Lock,
} from 'lucide-react';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { PermissionAction } from '../../types/prioritization/permissions';
import { CreateCanonicalClusterModal } from './item-view/CreateCanonicalClusterModal';
import { CreatePackageModal } from './item-view/CreatePackageModal';
import { AddDependencyModal } from './item-view/AddDependencyModal';
import { AddAggregationModal } from './item-view/AddAggregationModal';
import { AddElaborationModal } from './item-view/AddElaborationModal';
import { ProvenanceActionModal } from './item-view/ProvenanceActionModal';
import { getRelationshipGuideEntry } from '../../lib/relationshipGuide';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

const ELABORATION_GUIDE = getRelationshipGuideEntry('elaboration');

type ModalId = 'cluster' | 'package' | 'dependency' | 'aggregation' | 'elaboration' | 'lineage' | null;

interface RelationshipType {
  id: ModalId;
  label: string;
  bond: string;
  icon: React.ComponentType<{ className?: string }>;
  accentColor: string;
  badgeColor: string;
}

interface TriageRelationshipsPanelProps {
  itemId: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────────────────────────────────────

const RELATIONSHIP_TYPES: RelationshipType[] = [
  {
    id: 'cluster',
    label: 'Cluster',
    bond: 'Identity — duplicate or variant',
    icon: Layers,
    accentColor: 'text-[var(--text-ter)]',
    badgeColor: 'bg-[var(--slate-dim)] text-[var(--text-sec)] ring-blue-200',
  },
  {
    id: 'package',
    label: 'Package',
    bond: 'Atomicity — items that move together',
    icon: Package,
    accentColor: 'text-[var(--text-ter)]',
    badgeColor: 'bg-[var(--slate-dim)] text-[var(--text-sec)] ring-violet-200',
  },
  {
    id: 'dependency',
    label: 'Dependency',
    bond: 'Sequence — this item needs or blocks another',
    icon: Link2,
    accentColor: 'text-[var(--mustard)]',
    badgeColor: 'bg-[var(--mustard-dim)] text-[var(--mustard)] ring-amber-200',
  },
  {
    id: 'aggregation',
    label: 'Aggregation',
    bond: 'Composition — part of a larger initiative',
    icon: GitMerge,
    accentColor: 'text-[var(--green)]',
    badgeColor: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] ring-teal-200',
  },
  {
    id: 'elaboration',
    label: 'Elaboration',
    bond: ELABORATION_GUIDE?.tagline ?? 'Possibilities from minimally sufficient to ideal',
    icon: ELABORATION_GUIDE?.Icon ?? Puzzle,
    accentColor: 'text-[var(--coral)]',
    badgeColor: 'bg-[var(--coral-dim)] text-[var(--coral)] ring-rose-200',
  },
  {
    id: 'lineage',
    label: 'Lineage',
    bond: 'Succession — retire, split, merge, supersede',
    icon: GitBranch,
    accentColor: 'text-[var(--text-sec)]',
    badgeColor: 'bg-[var(--hover-tint)] text-[var(--text-sec)] ring-gray-200',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function TriageRelationshipsPanel({ itemId }: TriageRelationshipsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openModal, setOpenModal] = useState<ModalId>(null);

  const canPermission = useUserStore((s) => s.can) as (action: PermissionAction) => boolean;
  const canAttach = canPermission('items.triage.attach_relationships');

  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const getGroupsForItem = useItemRelationshipsStore((s) => s.getGroupsForItem);
  const getEdgesForItem = useItemRelationshipsStore((s) => s.getEdgesForItem);

  // Load relationships when the panel first expands or the item changes
  useEffect(() => {
    if (isExpanded) void fetchFromSupabase();
  }, [isExpanded, itemId, fetchFromSupabase]);

  const groups = useMemo(() => getGroupsForItem(itemId), [getGroupsForItem, itemId]);
  const edges = useMemo(() => getEdgesForItem(itemId), [getEdgesForItem, itemId]);

  const counts = useMemo(() => ({
    cluster: groups.filter((g) => g.type === 'cluster' && g.status !== 'dissolved').length,
    package: groups.filter((g) => g.type === 'package' && g.status !== 'dissolved').length,
    dependency: edges.filter((e) => e.type === 'dependency' && e.status !== 'dissolved').length,
    aggregation: edges.filter((e) => e.type === 'aggregation' && e.status !== 'dissolved').length,
    elaboration: edges.filter((e) => e.type === 'elaboration' && e.status !== 'dissolved').length,
    lineage: 0, // lineage is event-based; shown as action-only
  }), [groups, edges]);

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

  const handleModalClose = () => {
    setOpenModal(null);
    void fetchFromSupabase();
  };

  return (
    <>
      {/* Collapsible panel */}
      <div className="border border-[var(--rule)] overflow-hidden">
        <button
          onClick={() => setIsExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition text-left"
        >
          <div className="flex items-center gap-2">
            <GitMerge className={`h-4 w-4 ${canAttach ? 'text-[var(--text-ter)]' : 'text-[var(--mustard)]'}`} />
            <span className="text-sm font-medium text-[var(--text-sec)]">Attach relationships</span>
            {!canAttach && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--mustard)] font-medium">
                <Lock className="h-3 w-3" />
                <code className="bg-[var(--mustard-dim)] px-1 rounded">items.triage.attach_relationships</code>
              </span>
            )}
            {canAttach && totalCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)] ring-1 ring-indigo-200">
                {totalCount}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          )}
        </button>

        {isExpanded && (
          <div className="border-t border-[var(--rule)] bg-[var(--surface)] divide-y divide-[var(--rule)]">
            {RELATIONSHIP_TYPES.map((rel) => {
              const count = counts[rel.id as keyof typeof counts] ?? 0;
              const Icon = rel.icon;
              return (
                <div key={rel.id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex-shrink-0 ${rel.accentColor}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-pri)]">{rel.label}</span>
                        {count > 0 && (
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ring-1 ${rel.badgeColor}`}>
                            {count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-ter)] truncate">{rel.bond}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => canAttach && setOpenModal(rel.id)}
                    disabled={!canAttach}
                    title={!canAttach ? 'Requires items.triage.attach_relationships permission' : undefined}
                    className={`flex-shrink-0 inline-flex items-center gap-1  px-2.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40
                      ${rel.id === 'lineage'
                        ? 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                        : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border border-[var(--rule)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)]'
                      }`}
                  >
                    {rel.id === 'lineage' ? (
                      <>
                        <Clock className="h-3 w-3" />
                        Actions
                      </>
                    ) : (
                      <>
                        <Plus className="h-3 w-3" />
                        Add
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals — rendered outside the panel so they can be full-screen */}
      <CreateCanonicalClusterModal
        isOpen={openModal === 'cluster'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
      <CreatePackageModal
        isOpen={openModal === 'package'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
      <AddDependencyModal
        isOpen={openModal === 'dependency'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
      <AddAggregationModal
        isOpen={openModal === 'aggregation'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
      <AddElaborationModal
        isOpen={openModal === 'elaboration'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
      <ProvenanceActionModal
        isOpen={openModal === 'lineage'}
        onClose={handleModalClose}
        sourceItemId={itemId}
      />
    </>
  );
}
